/**
 * Mã QR đặt lịch của tiệm — thứ chủ tiệm in ra dán ở quầy, gương, cửa kính.
 *
 * Đường dẫn: `https://zalo.me/s/<Mini App ID>/?tiem=<mã tiệm>` — định dạng đã kiểm chứng ở
 * docs/zalo/deep-link-mini-app.md; Mini App ID chủ dự án xác nhận 21/09/2026. Mini App đọc `tiem`
 * rồi mở thẳng trang của tiệm (apps/miniapp/src/zalo.ts `docTiemTuDeepLink`).
 *
 * Tạo QR ngay trên máy (thư viện `uqr`, ADR-037) — không gửi mã tiệm cho dịch vụ QR bên ngoài.
 */
import { CalendarPlus, Copy, Download, Printer, QrCode, RefreshCw, Share2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { renderSVG } from 'uqr'
import { ApiFailure, cauHinh, type ToiLaAi, tiem } from './api.ts'
import { ghiDaMoMaQrTrenMay } from './da-mo-ma-qr.ts'
import { duongDanDatLich as dungDuongDan, linkVaTenFileMaQr } from './duong-dan-dat-lich.ts'
import { T } from './text/vi.ts'
import { ThongBao, To } from './ui.tsx'

/**
 * Mini App ID: CHỈ từ `ZALO_MINIAPP_ID` trong `.env` (vite.config.ts nạp lúc dựng, thiếu thì dừng dựng). Không có số
 * mặc định trong mã (chủ dự án đổi Mini App 29/09) — link QR luôn trỏ đúng Mini App đang cấu hình.
 */
const MINI_APP_ID: string = import.meta.env.VITE_ZALO_MINIAPP_ID

/** `chiNhanhId` (K6): QR của một chi nhánh mở sẵn đúng chi nhánh đó; không truyền = QR cả tiệm như cũ. */
export function duongDanDatLich(maTiem: string, chiNhanhId: string | null = null): string {
  return dungDuongDan(MINI_APP_ID, maTiem, chiNhanhId)
}

function dungLinkVaTenFile(p: {
  maDatLich: string | null
  maTiem: string
  chiNhanh: { id: string; ten: string } | null
}) {
  return linkVaTenFileMaQr({ miniAppId: MINI_APP_ID, ...p })
}

/** Vẽ SVG của QR lên canvas 1200px — ảnh gửi qua Zalo / in ở tiệm in đều dùng được. */
export async function veCanvas(svg: string): Promise<HTMLCanvasElement | null> {
  const co = 1200
  const anh = new Image()
  anh.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  await anh.decode()
  const canvas = document.createElement('canvas')
  canvas.width = co
  canvas.height = co
  const g = canvas.getContext('2d')
  if (g === null) return null
  g.fillStyle = '#fff'
  g.fillRect(0, 0, co, co)
  g.imageSmoothingEnabled = false
  g.drawImage(anh, 0, 0, co, co)
  return canvas
}

export async function taiPng(svg: string, tenFile: string): Promise<void> {
  const canvas = await veCanvas(svg)
  if (canvas === null) return
  const a = document.createElement('a')
  a.href = canvas.toDataURL('image/png')
  a.download = tenFile
  a.click()
}

/** Máy có bảng chia sẻ của hệ điều hành không (Android Chrome, iPhone Safari: có). */
const coChiaSe = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

/**
 * Mở bảng chia sẻ của máy với ẢNH mã QR + câu mời + đường dẫn — chủ tiệm chọn Zalo / Facebook là
 * gửi xong, không phải tải ảnh rồi mở app khác. Máy không gửi được ảnh (vd. Firefox) thì gửi chữ +
 * đường dẫn. `false` = không chia sẻ được (không tính trường hợp người dùng tự huỷ).
 */
async function chiaSe(
  svg: string,
  tenFile: string,
  tenTiem: string,
  link: string,
): Promise<boolean> {
  const chu = T.qrShareText(tenTiem, link)
  try {
    const canvas = await veCanvas(svg)
    const blob =
      canvas === null
        ? null
        : await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/png'))
    const file = blob === null ? null : new File([blob], tenFile, { type: 'image/png' })
    const kemAnh = file !== null && navigator.canShare?.({ files: [file] }) === true
    await navigator.share(
      kemAnh
        ? { title: tenTiem, text: chu, files: [file] }
        : { title: tenTiem, text: chu, url: link },
    )
    return true
  } catch (e) {
    // Người dùng tự đóng bảng chia sẻ: không phải lỗi, không báo gì.
    return e instanceof DOMException && e.name === 'AbortError'
  }
}

export function TheMaQr({ toi, onVeLich }: { toi: ToiLaAi; onVeLich?: () => void }) {
  /*
   * QR in MÃ ĐẶT LỊCH ngẫu nhiên (ADR-056), không in slug: mã đổi được khi QR bị dán sai chỗ.
   * Chưa tải xong / máy chủ cũ chưa có mã → tạm dùng slug (vẫn mở đúng tiệm).
   */
  const [ma, setMa] = useState<string | null>(null)
  const [hoiDoi, setHoiDoi] = useState(false)
  const [dangDoi, setDangDoi] = useState(false)
  useEffect(() => {
    void tiem
      .maDatLich()
      .then((x) => setMa(x.ma))
      .catch(() => setMa(null))
  }, [])
  /*
   * K6: tiệm > 1 chi nhánh đang dùng → hàng chip "Cả tiệm · A · B" phía trên mã; chọn chi nhánh thì link thêm `&cn=`
   * (QR dán ở cơ sở nào mở thẳng cơ sở đó). 1 chi nhánh → không có hàng chip, link như cũ. Không tải được → như cũ.
   */
  const [dsCn, setDsCn] = useState<{ id: string; ten: string }[]>([])
  const [cnQr, setCnQr] = useState<string | null>(null)
  useEffect(() => {
    void cauHinh
      .chiNhanh()
      .then((r) =>
        setDsCn(r.chiNhanh.filter((c) => c.dangDung).map((c) => ({ id: c.id, ten: c.ten }))),
      )
      .catch(() => setDsCn([]))
  }, [])
  const nhieuCn = dsCn.length > 1
  const cnDangChon = nhieuCn ? (dsCn.find((c) => c.id === cnQr) ?? null) : null
  async function doiMa() {
    setDangDoi(true)
    try {
      setMa((await tiem.doiMaDatLich()).ma)
      setHoiDoi(false)
      setBao(T.qrRotateDone)
    } catch (e) {
      setHoiDoi(false)
      setBao(e instanceof ApiFailure ? e.messageVi : T.qrShareFailed)
    } finally {
      setDangDoi(false)
    }
  }
  /*
   * Kế hoạch 10/2026, 1.5: ứng dụng đặt lịch trên Zalo CHƯA chạy thật (máy chủ không đặt cờ) thì không có tờ in,
   * không có nút gửi / tải cho khách. Vòng sửa UX Đợt 1: cũng KHÔNG hiện mã QR, đường dẫn, "mã đặt lịch" — chủ tiệm
   * cầm đường dẫn chưa chạy đi gửi khách là khách bấm vào ngõ cụt. Chỉ còn một khung giải thích. Thiếu cờ = chưa.
   */
  const live = toi.miniAppLive === true
  // ADR-118: chủ ĐÃ MỞ tờ QR (đang thấy mã thật) thì bước "Mã QR cho khách" của thẻ Chuẩn bị tiệm xong — nhớ theo (tiệm, máy).
  useEffect(() => {
    if (live) ghiDaMoMaQrTrenMay(toi.maTiem)
  }, [live, toi.maTiem])
  const { link, tenFile } = dungLinkVaTenFile({
    maDatLich: ma,
    maTiem: toi.maTiem,
    chiNhanh: cnDangChon,
  })
  const tenTrenTo = cnDangChon === null ? toi.tenTiem : `${toi.tenTiem} · ${cnDangChon.ten}`
  // Mức sửa lỗi Q (25%): QR dán ở quầy hay bị trầy, dính nước, chụp nghiêng.
  const svg = useMemo(
    () => renderSVG(link, { ecc: 'Q', border: 2, blackColor: '#1f1a1c', whiteColor: '#ffffff' }),
    [link],
  )
  const [bao, setBao] = useState<string | null>(null)

  async function chep() {
    try {
      await navigator.clipboard.writeText(link)
      setBao(T.qrCopied)
    } catch {
      setBao(T.qrCopyFailed)
    }
  }

  return (
    <>
      <section className="the the-dem">
        <div className="the-tieu-de">
          <div>
            <h2>{T.qrTitle}</h2>
            {live && <p className="qr-mo-ta">{T.qrHint}</p>}
          </div>
        </div>

        {!live && (
          <div className="qr-chua-live">
            {/* Trạng thái chờ bình thường, không phải lỗi: tông thông tin + một việc làm được ngay (vòng 33 N1). */}
            <ThongBao loai="thong-tin" lon>
              {T.qrChuaLive}
            </ThongBao>
            {onVeLich !== undefined && (
              <button type="button" className="nut nut-chinh nut-lon" onClick={onVeLich}>
                <CalendarPlus size={20} />
                {T.qrChuaLiveNut}
              </button>
            )}
          </div>
        )}

        {live && nhieuCn && (
          <div className="qr-chon-chi-nhanh">
            <p className="qr-mo-ta">{T.qrChoBranch}</p>
            <div className="hang-chip">
              <button
                type="button"
                className={`chip ${cnDangChon === null ? 'dang-chon' : ''}`}
                aria-pressed={cnDangChon === null}
                onClick={() => setCnQr(null)}
              >
                {T.qrWholeShop}
              </button>
              {dsCn.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`chip ${cnDangChon?.id === c.id ? 'dang-chon' : ''}`}
                  aria-pressed={cnDangChon?.id === c.id}
                  onClick={() => setCnQr(c.id)}
                >
                  {c.ten}
                </button>
              ))}
            </div>
            <p className="qr-mo-ta qr-goi-y-chi-nhanh">{T.qrBranchTip}</p>
          </div>
        )}

        {live && (
          <div className="qr-bo-cuc">
            {/* Chỉ khối này được in (styles.css @media print). Chỉ có khi đã chạy thật trên Zalo. */}
            <div className="to-dan-quay" id="to-dan-quay">
              <p className="tdq-ten">{tenTrenTo}</p>
              <p className="tdq-loi">{T.qrPosterLead}</p>
              {/* biome-ignore lint/security/noDangerouslySetInnerHtml: SVG do uqr tạo từ đường dẫn của chính mình, không có dữ liệu người dùng nhập. */}
              <div className="tdq-ma" dangerouslySetInnerHTML={{ __html: svg }} />
              <ol className="tdq-buoc">
                <li>{T.qrStep1}</li>
                <li>{T.qrStep2}</li>
                <li>{T.qrStep3}</li>
              </ol>
              <p className="tdq-chan">{T.qrPosterFoot}</p>
            </div>

            <div className="qr-thao-tac">
              <button
                type="button"
                className="nut nut-chinh nut-lon"
                onClick={() => window.print()}
              >
                <Printer size={20} />
                {T.qrPrint}
              </button>
              {coChiaSe && (
                <button
                  type="button"
                  className="nut nut-vien nut-lon"
                  onClick={() =>
                    void chiaSe(svg, tenFile, tenTrenTo, link).then((ok) => {
                      if (!ok) setBao(T.qrShareFailed)
                    })
                  }
                >
                  <Share2 size={20} />
                  {T.qrShare}
                </button>
              )}
              <button
                type="button"
                className="nut nut-vien nut-lon"
                onClick={() => void taiPng(svg, tenFile)}
              >
                <Download size={20} />
                {T.qrDownload}
              </button>
              {/* Bảng chia sẻ của máy đã có "Sao chép" — chỉ hiện nút riêng khi máy không chia sẻ được. */}
              {!coChiaSe && (
                <button type="button" className="nut nut-vien nut-lon" onClick={() => void chep()}>
                  <Copy size={20} />
                  {T.qrCopy}
                </button>
              )}
              <div className="qr-link">
                <QrCode size={18} />
                <span>{link}</span>
              </div>
              {ma !== null && <p className="qr-ma">{T.qrCodeLabel(ma)}</p>}
              {ma !== null && (
                // Nút phụ nhạt: đổi mã làm hỏng tờ QR đã in — không đứng ngang hàng "Tải ảnh" (ui-critic v21).
                <button
                  type="button"
                  className="nut nut-nhat nut-lon"
                  onClick={() => setHoiDoi(true)}
                >
                  <RefreshCw size={18} />
                  {T.qrRotate}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
      {bao !== null && (
        <ThongBao loai="thanh-cong" noi lon onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {hoiDoi && (
        <To nhan={T.qrRotateTitle} onDong={() => setHoiDoi(false)}>
          <div className="to-than">
            <h2>{T.qrRotateTitle}</h2>
            <ThongBao loai="canh-bao">{T.qrRotateWarn}</ThongBao>
          </div>
          <div className="to-chan">
            <button type="button" className="nut nut-vien" onClick={() => setHoiDoi(false)}>
              {T.cancel}
            </button>
            <button
              type="button"
              className="nut nut-chinh"
              disabled={dangDoi}
              onClick={() => void doiMa()}
            >
              {T.qrRotateOk}
            </button>
          </div>
        </To>
      )}
    </>
  )
}
